import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Modal from '@/components/common/Modal/Modal';
import DepositTab from './tabs/DepositTab';
import WithdrawTab from './tabs/WithdrawTab';
import SwapTab from './tabs/SwapTab';
import styles from './WalletModal.module.css';

/**
 * WalletModal Component
 * Modal for wallet operations (deposit, withdraw, swap)
 * Opens via query params: /wallet?mode=deposit, /wallet?mode=withdraw, /wallet?mode=swap
 * Full screen on mobile, modal on desktop
 */
const WalletModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  
  // Initialize activeTab from mode immediately, default to 'deposit'
  const getInitialTab = (initialMode) => {
    if (initialMode === 'deposit' || initialMode === 'withdraw' || initialMode === 'swap') {
      return initialMode;
    }
    return 'deposit';
  };
  
  // Set initial state immediately from URL params - read directly from window.location for instant initialization
  const [activeTab, setActiveTab] = useState(() => {
    // Get mode from current URL search params immediately
    const urlParams = new URLSearchParams(window.location.search);
    const initialMode = urlParams.get('mode');
    return getInitialTab(initialMode);
  }); // 'deposit' | 'withdraw' | 'swap'
  
  const [isOpen, setIsOpen] = useState(() => {
    // Check if modal should be open on initial mount - read directly from URL
    const urlParams = new URLSearchParams(window.location.search);
    const initialMode = urlParams.get('mode');
    return location.pathname === '/wallet' && (initialMode === 'deposit' || initialMode === 'withdraw' || initialMode === 'swap');
  });

  useEffect(() => {
    // Check if modal should be open based on query params
    if (location.pathname === '/wallet' && mode) {
      if (mode === 'deposit' || mode === 'withdraw' || mode === 'swap') {
        setIsOpen(true);
        // Always sync activeTab with mode from URL immediately - no delay
        setActiveTab(mode);
      } else {
        setIsOpen(false);
      }
    } else {
      setIsOpen(false);
    }
  }, [location.pathname, mode]);

  const handleClose = () => {
    setIsOpen(false);
    // Remove mode query param and navigate to home
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('mode');
    navigate(`/?${newSearchParams.toString()}`, { replace: true });
  };

  const switchTab = (tab) => {
    // Update state immediately for instant UI feedback
    setActiveTab(tab);
    // Update query param to sync with URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('mode', tab);
    navigate(`/wallet?${newSearchParams.toString()}`, { replace: true });
  };

  const getModalTitle = () => {
    switch (activeTab) {
      case 'deposit':
        return 'Deposit';
      case 'withdraw':
        return 'Withdraw';
      case 'swap':
        return 'Swap';
      default:
        return 'Wallet';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      style={{ backgroundColor: 'transparent' }}
      title={getModalTitle()}
      showCloseButton={true}
    >
      <div className={styles.modalBody}>
        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'deposit' ? styles.tabActive : ''}`}
            onClick={() => switchTab('deposit')}
          >
            Deposit
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'withdraw' ? styles.tabActive : ''}`}
            onClick={() => switchTab('withdraw')}
          >
            Withdraw
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'swap' ? styles.tabActive : ''}`}
            onClick={() => switchTab('swap')}
          >
            Swap
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === 'deposit' && <DepositTab />}
          {activeTab === 'withdraw' && <WithdrawTab />}
          {activeTab === 'swap' && <SwapTab />}
        </div>
      </div>
    </Modal>
  );
};

export default WalletModal;
